import { createReducer, on } from '@ngrx/store';
import { BusinessInformationEntity, BusinessSearchFilters } from 'src/app/models/business.model';
import * as BusinessActions from './business.actions';

export interface BusinessState {
  businesses: BusinessInformationEntity[];
  filteredBusinesses: BusinessInformationEntity[];
  filters: BusinessSearchFilters;
  lastDocumentId: string | null;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  currentZipCode: string | null;
  // Search location coordinates
  searchLatitude: number | null;
  searchLongitude: number | null;
  // Pagination state
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

const initialState: BusinessState = {
  businesses: [],
  filteredBusinesses: [],
  filters: {
    searchTerm: null,
    type: null,
    zipCode: null,
  },
  lastDocumentId: null,
  hasMore: true,
  isLoading: false,
  isLoadingMore: false,
  error: null,
  currentZipCode: null,
  searchLatitude: null,
  searchLongitude: null,
  totalCount: 0,
  totalPages: 0,
  currentPage: 1,
};

export const businessReducer = createReducer(
  initialState,
  on(BusinessActions.loadBusinessesByZipCode, (state, { zipCode }) => ({
    ...state,
    isLoading: true,
    error: null,
    currentZipCode: zipCode,
    filters: {
      ...state.filters,
      zipCode,
    },
  })),
  on(BusinessActions.loadBusinessesByZipCodeSuccess, (state, { businesses, lastDocumentId, hasMore, totalCount, totalPages, currentPage, latitude, longitude }) => {
    const seen = new Set<string>();
    const uniqueBusinesses = businesses.filter((b) => {
      if (seen.has(b.id)) {
        return false;
      }
      seen.add(b.id);
      return true;
    });

    return {
      ...state,
      businesses: uniqueBusinesses,
      filteredBusinesses: uniqueBusinesses,
      lastDocumentId,
      hasMore,
      // Update pagination state if provided
      totalCount: totalCount !== undefined ? totalCount : state.totalCount,
      totalPages: totalPages !== undefined ? totalPages : state.totalPages,
      currentPage: currentPage !== undefined ? currentPage : state.currentPage,
      // Update search location if provided
      searchLatitude: latitude !== undefined ? latitude : state.searchLatitude,
      searchLongitude: longitude !== undefined ? longitude : state.searchLongitude,
      isLoading: false,
      error: null,
    };
  }),
  on(BusinessActions.loadBusinessesByZipCodeFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(BusinessActions.searchBusinesses, (state, { filters }) => ({
    ...state,
    isLoading: true,
    error: null,
    filters,
  })),
  on(BusinessActions.searchBusinessesSuccess, (state, { businesses }) => {
    const seen = new Set<string>();
    const uniqueBusinesses = businesses.filter((b) => {
      if (seen.has(b.id)) {
        return false;
      }
      seen.add(b.id);
      return true;
    });

    return {
      ...state,
      businesses: uniqueBusinesses,
      filteredBusinesses: uniqueBusinesses,
      isLoading: false,
      error: null,
    };
  }),
  on(BusinessActions.searchBusinessesFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(BusinessActions.loadMoreBusinesses, (state) => ({
    ...state,
    isLoadingMore: true,
    error: null,
  })),
  on(BusinessActions.loadMoreBusinessesSuccess, (state, { businesses, lastDocumentId, hasMore }) => {
    const seen = new Set<string>(state.businesses.map((b) => b.id));
    const newBusinesses = businesses.filter((b) => {
      if (seen.has(b.id)) {
        return false;
      }
      seen.add(b.id);
      return true;
    });

    return {
      ...state,
      businesses: [...state.businesses, ...newBusinesses],
      filteredBusinesses: [...state.filteredBusinesses, ...newBusinesses],
      lastDocumentId,
      hasMore,
      isLoadingMore: false,
      error: null,
    };
  }),
  on(BusinessActions.loadMoreBusinessesFailure, (state, { error }) => ({
    ...state,
    isLoadingMore: false,
    error,
  })),
  on(BusinessActions.loadBusinessesByLocation, (state, { latitude, longitude }) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(BusinessActions.loadBusinessesByLocationSuccess, (state, { businesses, lastDocumentId, hasMore }) => {
    const seen = new Set<string>();
    const uniqueBusinesses = businesses.filter((b) => {
      if (seen.has(b.id)) {
        return false;
      }
      seen.add(b.id);
      return true;
    });

    return {
      ...state,
      businesses: uniqueBusinesses,
      filteredBusinesses: uniqueBusinesses,
      lastDocumentId,
      hasMore,
      isLoading: false,
      error: null,
    };
  }),
  on(BusinessActions.loadBusinessesByLocationFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(BusinessActions.loadMoreBusinessesByLocation, (state) => ({
    ...state,
    isLoadingMore: true,
    error: null,
  })),
  on(BusinessActions.loadMoreBusinessesSuccess, (state, { businesses, lastDocumentId, hasMore }) => {
    const seen = new Set<string>(state.businesses.map((b) => b.id));
    const newBusinesses = businesses.filter((b) => {
      if (seen.has(b.id)) {
        return false;
      }
      seen.add(b.id);
      return true;
    });

    return {
      ...state,
      businesses: [...state.businesses, ...newBusinesses],
      filteredBusinesses: [...state.filteredBusinesses, ...newBusinesses],
      lastDocumentId,
      hasMore,
      isLoading: false,
      error: null,
    };
  }),
  on(BusinessActions.clearBusinesses, () => initialState),
  on(BusinessActions.loadBusinessesByLocationPage, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(BusinessActions.loadBusinessesByLocationPageSuccess, (state, { businesses, lastDocumentId, hasMore }) => {
    const seen = new Set<string>(state.businesses.map((b) => b.id));
    const newBusinesses = businesses.filter((b) => {
      if (seen.has(b.id)) {
        return false;
      }
      seen.add(b.id);
      return true;
    });

    return {
      ...state,
      businesses: [...state.businesses, ...newBusinesses],
      filteredBusinesses: [...state.filteredBusinesses, ...newBusinesses],
      lastDocumentId,
      hasMore,
      isLoading: false,
      error: null,
    };
  }),
  on(BusinessActions.loadBusinessesByLocationPageFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(BusinessActions.loadBusinessesByLocationPaginated, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(BusinessActions.loadBusinessesByLocationPaginatedSuccess, (state, { businesses, totalCount, totalPages, currentPage, latitude, longitude }) => {
    return {
      ...state,
      businesses: businesses,
      filteredBusinesses: businesses,
      totalCount,
      totalPages,
      currentPage,
      hasMore: currentPage < totalPages,
      // Update search location if provided
      searchLatitude: latitude !== undefined ? latitude : state.searchLatitude,
      searchLongitude: longitude !== undefined ? longitude : state.searchLongitude,
      isLoading: false,
      error: null,
    };
  }),
  on(BusinessActions.loadBusinessesByLocationPaginatedFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  }))
);

